import { ArrowLeft, ArrowRight } from 'almanac-ui/icons'
import { Form } from '../../Form/Form'
import { Field } from 'components/Wizards/Field/Field'
import Styles from './ScheduleContent.module.scss'
import dayjs, { Dayjs } from 'dayjs'
import { useState } from 'react'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { Button } from 'blaze-ui/Button'
import { CampaignNameAndLocation } from './components/CampaignNameAndLocation/CampaignNameAndLocation'
import { AlmanacFile, Archetype } from 'types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useModals } from 'almanac-ui/Modal'
import { DayAndTime } from './components/StartDateAndPostFrequency/StartDateAndPostFrequencyModal'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { StepSchema } from 'components/Wizards/schemas/Types'
import { FormSchema } from 'components/Wizards/Wizard/Form/Types'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useExternalIntegrationsRealtime } from '../ExternalIntegrationField/useExternalIntegrationsRealtime'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { BLAZE_HOME_PATH } from 'paths'
import { generatePath } from 'react-router-dom'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { NoAccountWarningModal } from './components/NoAccountWarningModal/NoAccountWarningModal'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { StartDatePostFrequencyField } from './components/StartDatePostFrequencyField/StartDatePostFrequencyField'
import { Entry } from './components/Entry/Entry'

export type ScheduledContentStatus = 'successful' | 'failed'

export interface ScheduledContent {
  uuid: string
  text: string
  postAt: Dayjs
  sourceVisualElementId?: number
  previewUrl?: string
  initialMediaFileId?: string
  hasReplaceableImage?: boolean
  // The generated content for a design
  content?: any
  archetype?: Archetype
  width?: number
  height?: number
  status?: ScheduledContentStatus
}

export enum PostingCadence {
  Once = 'Post once per week',
  Twice = 'Post twice per week (T/Th)',
  Thrice = 'Post three times per week (M/W/F)',
  Weekdays = 'Post once per weekday',
  Everyday = 'Post once per day',
  Custom = 'custom',
}

interface Props {
  header?: JSX.Element | null
  scheduledContent: ScheduledContent[]
  setScheduledContent: React.Dispatch<React.SetStateAction<ScheduledContent[]>>
  externalIntegrationType?: ExternalIntegrationEnum
  onSubmit: () => void
  goBack: () => void
  inputPlaceholder: string
  initialPostingCadence: PostingCadence
  onScheduleChange: (schedule: { startDate: Dayjs; daysAndTimes: DayAndTime[] }) => void
  setCampaignName?: (name: string) => void
  campaignName?: string | undefined
  onSelectedFolder?: (folder: AlmanacFile) => void
  selectedFolder?: AlmanacFile | undefined
  previousLabel?: string
  submitLabel?: string
  lineClamp?: number
  initialStartDate?: Dayjs
  schema: StepSchema | FormSchema
  externalIntegrationId?: number
  onSelectExternalIntegrationId?: (id: number) => void
}

export const ScheduleContent = ({
  header = null,
  scheduledContent,
  setScheduledContent,
  externalIntegrationType,
  onSubmit,
  goBack,
  inputPlaceholder,
  initialPostingCadence,
  onScheduleChange,
  setCampaignName,
  onSelectedFolder,
  selectedFolder,
  previousLabel = 'Previous Step',
  submitLabel = 'Generate Posts',
  campaignName,
  lineClamp = 2,
  initialStartDate = dayjs().minute(0).second(0),
  schema,
  externalIntegrationId,
  onSelectExternalIntegrationId,
}: Props) => {
  const [isCalendarOpenFor, setIsCalendarOpenFor] = useState<string>()
  const [isEditingCaptionFor, setIsEditingCaptionFor] = useState<string>()
  const { isMobile } = useResponsiveDesign()
  const { openModal, closeModal } = useModals()
  const { data, refetch } = useExternalIntegrations()
  const workspaceId = useCurrentWorkspaceId()
  const openWindowSafely = useOpenWindowSafely()
  const isDesktopWizardsUpdatesOn = useFlipperFlag('desktop_wizards_updates')

  useExternalIntegrationsRealtime({
    workspaceId,
    onMessage: async () => {
      const result = await refetch()
      const matchingIntegration = result.data?.external_integrations.find(
        (integration) => integration.provider === externalIntegrationType,
      )
      if (matchingIntegration && onSelectExternalIntegrationId) {
        onSelectExternalIntegrationId(matchingIntegration.id)
      }
    },
  })

  const openIntegrationsInNewTab = () => {
    if (!workspaceId) return

    const path = generatePath(BLAZE_HOME_PATH, { workspaceId })

    openWindowSafely(`${path}?modal=integrations`)
  }

  const matchingIntegration = data?.external_integrations.find(
    (i) => i.id === externalIntegrationId && i.provider === externalIntegrationType,
  )

  const onConnect = () => {
    openIntegrationsInNewTab()
    closeModal()
  }

  const handleSubmit = () => {
    if (!matchingIntegration && externalIntegrationType) {
      openModal(
        NoAccountWarningModal,
        {
          platform: externalIntegrationType,
          onConnect,
          onCancel: closeModal,
          onScheduleDrafts: onSubmit,
        },
        {
          newStack: true,
        },
      )
    } else {
      onSubmit()
    }
  }

  return (
    <Form.Root>
      <Form.Section>
        <Form.SectionContent>
          <Form.Fields className={Styles.scheduleContentFields}>
            <div className={Styles.fieldsWrapper}>
              {header}
              {scheduledContent.length > 1 && (
                <StartDatePostFrequencyField
                  initialPostingCadence={initialPostingCadence}
                  initialStartDate={initialStartDate}
                  onScheduleChange={onScheduleChange}
                  externalIntegrationType={externalIntegrationType}
                  externalIntegration={matchingIntegration}
                />
              )}
              <div className={Styles.scheduledContentGroup}>
                {scheduledContent.map((content, index) => (
                  <Entry
                    key={`content-${content.uuid}`}
                    index={index}
                    content={content}
                    lineClamp={lineClamp}
                    inputPlaceholder={inputPlaceholder}
                    isCalendarOpen={isCalendarOpenFor === content.uuid}
                    onGenerate={(value: ScheduledContentStatus) => {
                      setScheduledContent((prev) =>
                        prev.map((c) => (c.uuid === content.uuid ? { ...c, status: value } : c)),
                      )
                    }}
                    onCalendarOpenChange={(isOpen) => {
                      if (isOpen) {
                        setIsCalendarOpenFor(content.uuid)
                        return
                      }

                      setIsCalendarOpenFor(undefined)
                    }}
                    isEditingCaption={isEditingCaptionFor === content.uuid}
                    onEditingCaptionChange={(isEditing) => {
                      if (isEditing) {
                        setIsEditingCaptionFor(content.uuid)
                        return
                      }

                      setIsEditingCaptionFor(undefined)
                    }}
                    onUpdateCaption={(text) => {
                      setScheduledContent(scheduledContent.map((c) => (c.uuid === content.uuid ? { ...c, text } : c)))
                      setIsEditingCaptionFor(undefined)
                    }}
                    scheduledContent={scheduledContent}
                    setScheduledContent={setScheduledContent}
                    onUpdateDesignContent={(designContent) => {
                      setScheduledContent((prev) =>
                        prev.map((c) => (c.uuid === content.uuid ? { ...c, content: designContent } : c)),
                      )
                    }}
                  />
                ))}
              </div>
            </div>
            {onSelectedFolder && setCampaignName && (
              <Field label="Name your campaign and select a location" isRequired>
                <CampaignNameAndLocation
                  campaignName={campaignName}
                  onSelectedFolder={onSelectedFolder}
                  setCampaignName={setCampaignName}
                  selectedFolder={selectedFolder}
                />
              </Field>
            )}
          </Form.Fields>
        </Form.SectionContent>
      </Form.Section>

      <Form.Actions isSticky>
        <ResponsiveDesign.Desktop>
          <Form.PreviousButton variant={isDesktopWizardsUpdatesOn ? 'editor' : 'secondary'} onPress={goBack}>
            Previous Step
          </Form.PreviousButton>
        </ResponsiveDesign.Desktop>
        <Form.PrimaryActions>
          <Button
            size="lg"
            onPress={handleSubmit}
            endIcon={ArrowRight}
            isDisabled={
              (campaignName?.length || 0) === 0 ||
              (scheduledContent.every((c) => !!c.width && !!c.height) &&
                (scheduledContent.every((c) => c.status === 'failed') || scheduledContent.some((c) => !c.status)))
            }
            fullWidth={isMobile}
            aria-label={submitLabel}
          >
            {submitLabel}
          </Button>
          <ResponsiveDesign.Mobile>
            <Button frontIcon={ArrowLeft} variant="secondary" size="lg" onPress={goBack} fullWidth={isMobile}>
              {previousLabel}
            </Button>
          </ResponsiveDesign.Mobile>
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
}
